import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints } from "../config/api";

const AgentCategoryModify = ({ onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchCategories = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setError("Please log in to view categories.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(endpoints.agentsCategories, {
        headers: { ...authHeaders, Accept: "*/*", "Content-Type": "application/json" },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setCategories(list.map((c) => (typeof c === "string" ? c : String(c))));
    } catch (err) {
      console.error("Error fetching categories", err);
      setError(getErrorMessage(err, "Failed to fetch categories."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openEdit = (name) => {
    setEditingCategory(name);
    setNewName(name);
    setError("");
  };

  const closeEdit = () => {
    setEditingCategory(null);
    setNewName("");
  };

  const handleRename = async () => {
    const trimmed = (newName || "").trim();
    if (!editingCategory || !trimmed || trimmed === editingCategory) {
      closeEdit();
      return;
    }
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setError("Please log in to rename categories.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.patch(
        endpoints.agentsCategories,
        { oldName: editingCategory, newName: trimmed },
        {
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
        }
      );
      setCategories((prev) =>
        prev.map((c) => (c === editingCategory ? trimmed : c))
      );
      closeEdit();
    } catch (err) {
      console.error("Error renaming category", err);
      setError(getErrorMessage(err, "Failed to rename category."));
    } finally {
      setSaving(false);
    }
  };

  const tableWrapperStyle = {
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: "#f3f4f6",
    fontWeight: "600",
    color: "#374151",
    borderBottom: "2px solid #e5e7eb",
  };

  const tdStyle = {
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1f2937",
  };

  const editButtonStyle = {
    padding: "6px 14px",
    fontSize: "13px",
    backgroundColor: "#3730a3",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const closeButtonStyle = {
    padding: "10px 20px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const errorMessageStyle = {
    color: "#ef4444",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#fee2e2",
    borderRadius: "6px",
    border: "1px solid #ef4444",
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalContentStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "24px",
    maxWidth: "400px",
    width: "90%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  };

  const modalTitleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "16px",
  };

  const modalInputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    boxSizing: "border-box",
    marginBottom: "16px",
  };

  const modalButtonsStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  };

  const modalCancelButtonStyle = {
    padding: "8px 16px",
    fontSize: "14px",
    backgroundColor: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2 style={{ margin: 0, fontSize: "20px", color: "#1f2937" }}>Agent Category Modify</h2>
        <button type="button" style={closeButtonStyle} onClick={onClose}>
          Close
        </button>
      </div>

      {error && <div style={errorMessageStyle}>{error}</div>}

      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading categories…</p>
      ) : (
        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Category</th>
                <th style={{ ...thStyle, width: "120px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ ...tdStyle, color: "#6b7280", textAlign: "center" }}>
                    No categories found.
                  </td>
                </tr>
              ) : (
                categories.map((name) => (
                  <tr key={name}>
                    <td style={tdStyle}>{name}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button
                        type="button"
                        style={editButtonStyle}
                        onClick={() => openEdit(name)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {editingCategory && (
        <div style={modalOverlayStyle} onClick={closeEdit}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>Rename category</div>
            <div style={{ marginBottom: "8px", fontSize: "13px", color: "#6b7280" }}>
              Current: <strong>{editingCategory}</strong>
            </div>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New category name"
              style={modalInputStyle}
              autoFocus
            />
            <div style={modalButtonsStyle}>
              <button type="button" style={modalCancelButtonStyle} onClick={closeEdit} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                style={editButtonStyle}
                onClick={handleRename}
                disabled={saving || !(newName || "").trim() || (newName || "").trim() === editingCategory}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentCategoryModify;
